import { GetBeneficiary } from '@app/finxone-web-frontend/app/lib/actions/beneficiary.action';
import { ResetFormDataAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { BeneficiaryViewType, FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  BeneficiaryDataContext,
  isMultiCurrencyAccountInfoType,
  ViewType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

export interface BeneficiaryScrollHandlerParams {
  store: Store;
  destroy$: Subject<void>;

  dataContext: BeneficiaryDataContext;
  viewType: ViewType;
  beneficiaryViewType: BeneficiaryViewType;

  pageNumber: number;
  pageSize: number;
  searchText?: string;

  beneficiariesCurrency?: string;

  setPageNumber: (page: number) => void;
  setCurrency: (currency: string) => void;
}

interface BeneficiaryListHandlerParams {
  store: Store;
  destroy$: Subject<void>;

  dataContext: BeneficiaryDataContext;
  viewType: ViewType;
  beneficiaryViewType: BeneficiaryViewType;

  pageNumber: number;
  pageSize: number;

  setCurrency: (currency: string) => void;
}

interface BeneficiarySearchContextParams {
  store: Store;
  viewType: ViewType;
  pageSize: number;
  beneficiaryViewType: BeneficiaryViewType;
  destroy$: Subject<void>;
}

interface BeneficiarySearchContextConfig {
  currency$: () => Observable<string | undefined>;
  resolveCurrency: (currency?: string) => string | undefined;
  resolvePageSize: () => number;
}

const DROPDOWN_PAGE_SIZE = 100;

const resolvePageLimit = (viewType: ViewType, pageSize: number): number =>
  viewType === ViewType.DROPDOWN ? DROPDOWN_PAGE_SIZE : pageSize;

const selectedCurrency$ = (store: Store, destroy$: Subject<void>): Observable<string> =>
  store
    .select(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.BENEFICIARY_PAYMENT_CURRENCY_SELECTION),
    )
    .pipe(
      map((state) => state?.formData?.selectedCurrency ?? ''),
      distinctUntilChanged(),
      takeUntil(destroy$),
    );

const dispatchGetBeneficiary = (
  store: Store,
  params: {
    pageNumber: number;
    pageSize: number;
    searchText?: string;
    isLoaderShown?: boolean;
    currency?: string;
    viewType: ViewType;
    beneficiaryViewType: BeneficiaryViewType;
    dataContext?: BeneficiaryDataContext;
  },
): void => {
  store.dispatch(
    new GetBeneficiary(
      params.pageNumber,
      params.pageSize,
      params.searchText,
      params.isLoaderShown,
      params.currency,
      params.viewType,
      params.beneficiaryViewType,
      params.dataContext,
    ),
  );
};

export function createBeneficiarySearchConfig(
  params: BeneficiarySearchContextParams,
): Record<string, BeneficiarySearchContextConfig> {
  const { store, viewType, pageSize, destroy$ } = params;

  const defaultConfig: BeneficiarySearchContextConfig = {
    currency$: () => of(''),
    resolveCurrency: (currency?: string) => currency,
    resolvePageSize: () => resolvePageLimit(viewType, pageSize),
  };

  return {
    [BeneficiaryDataContext.BENEFICIARY_CURRENCY_CONTEXT]: {
      currency$: () => selectedCurrency$(store, destroy$),

      resolveCurrency: (currency: string | undefined) => currency,

      resolvePageSize: () => pageSize,
    },

    [BeneficiaryDataContext.ALL_BENEFICIARIES]: {
      currency$: () => of(''),
      resolveCurrency: () => undefined,
      resolvePageSize: () => resolvePageLimit(viewType, pageSize),
    },

    [BeneficiaryDataContext.CURRENT_ACCOUNT_CONTEXT]: defaultConfig,

    DEFAULT: defaultConfig,
  };
}

export const beneficiaryListHandlers: Record<
  BeneficiaryDataContext,
  (params: BeneficiaryListHandlerParams) => void
> = {
  [BeneficiaryDataContext.ALL_BENEFICIARIES]: ({
    store,
    viewType,
    beneficiaryViewType,
    pageNumber,
    pageSize,
  }) => {
    dispatchGetBeneficiary(store, {
      pageNumber,
      pageSize: resolvePageLimit(viewType, pageSize),
      viewType,
      beneficiaryViewType,
      dataContext: BeneficiaryDataContext.ALL_BENEFICIARIES,
    });
  },

  [BeneficiaryDataContext.CURRENT_ACCOUNT_CONTEXT]: ({
    store,
    destroy$,
    viewType,
    beneficiaryViewType,
    pageNumber,
    pageSize,
    setCurrency,
  }) => {
    // Skip resetting on the first emission (the replayed current value on mount/remount) -
    // otherwise navigating back to a page hosting this widget wipes in-progress form data
    // (e.g. payment reason/reference) even though the account never actually changed.
    let hasEmittedOnce = false;
    store
      .select(AccountState.getCurrentAccount)
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(destroy$),
      )
      .subscribe((account) => {
        const currency =
          account !== undefined && isMultiCurrencyAccountInfoType(account)
            ? account?.currentMultiCurrencyCode ?? ''
            : account?.currency ?? 'GBP';

        setCurrency(currency);

        dispatchGetBeneficiary(store, {
          pageNumber,
          pageSize: resolvePageLimit(viewType, pageSize),
          currency,
          viewType,
          beneficiaryViewType,
        });

        if (hasEmittedOnce) {
          store.dispatch(new ResetFormDataAction());
        }
        hasEmittedOnce = true;
      });
  },

  [BeneficiaryDataContext.BENEFICIARY_CURRENCY_CONTEXT]: ({
    store,
    destroy$,
    viewType,
    beneficiaryViewType,
    pageSize,
    setCurrency,
  }) => {
    selectedCurrency$(store, destroy$).subscribe((currency) => {
      setCurrency(currency);

      dispatchGetBeneficiary(store, {
        pageNumber: 1,
        pageSize: resolvePageLimit(viewType, pageSize),
        currency,
        viewType,
        beneficiaryViewType,
      });
    });
  },
};

export const beneficiaryScrollHandlers: Record<
  BeneficiaryDataContext,
  (params: BeneficiaryScrollHandlerParams) => void
> = {
  [BeneficiaryDataContext.BENEFICIARY_CURRENCY_CONTEXT]: ({
    store,
    destroy$,
    pageNumber,
    pageSize,
    searchText,
    viewType,
    beneficiaryViewType,
    setCurrency,
  }) => {
    selectedCurrency$(store, destroy$).subscribe((currency) => {
      setCurrency(currency);

      dispatchGetBeneficiary(store, {
        pageNumber,
        pageSize,
        searchText,
        currency,
        isLoaderShown: false,
        viewType,
        beneficiaryViewType,
      });
    });
  },

  [BeneficiaryDataContext.CURRENT_ACCOUNT_CONTEXT]: ({
    store,
    pageNumber,
    pageSize,
    searchText,
    beneficiariesCurrency,
    viewType,
    beneficiaryViewType,
  }) => {
    dispatchGetBeneficiary(store, {
      pageNumber,
      pageSize,
      searchText,
      currency: beneficiariesCurrency,
      isLoaderShown: false,
      viewType,
      beneficiaryViewType,
    });
  },

  [BeneficiaryDataContext.ALL_BENEFICIARIES]: ({
    store,
    pageNumber,
    pageSize,
    searchText,
    viewType,
    beneficiaryViewType,
    dataContext,
  }) => {
    dispatchGetBeneficiary(store, {
      pageNumber,
      pageSize,
      searchText,
      isLoaderShown: false,
      viewType,
      beneficiaryViewType,
      dataContext,
    });
  },
};
